import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import CheckDuplicateIcon from "src/core/assets/images/icons/check-duplicate.svg?react";
import CheckIcon from "src/core/assets/images/icons/check.svg?react";
import CloseFullIcon from "src/core/assets/images/icons/close-full.svg?react";
import NextIcon from "src/core/assets/images/icons/next.svg?react";
import { StakeholderCard } from "src/core/components/folder/stakeholders/StakeholderCard";
import { Button } from "src/core/components/ui/button";
import { AdvancedPersonWithEReputationStatus } from "src/core/constants/folder";
import { IRiskOption } from "src/core/constants/options";
import { cn } from "src/core/lib/utils";

type DuplicateCandidateDecision = "different" | "same";

export type DuplicateCandidateCardProps = {
  candidate: AdvancedPersonWithEReputationStatus;
  index: number;
  total: number;

  countryWithRiskOptions: IRiskOption[];
  customerCompanyNames?: string[];
  customerCompaniesLoading?: boolean;

  canGoPrev?: boolean;
  canGoNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;

  selectedDecision?: DuplicateCandidateDecision;
  onMarkDifferent?: () => void;
  onMarkSame?: () => void;
  isReferenceSelected?: boolean;
  onSelectReference?: () => void;
};

export const DuplicateCandidateCard = ({
  candidate,
  index,
  total,
  countryWithRiskOptions,
  customerCompanyNames = [],
  customerCompaniesLoading = false,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  selectedDecision,
  onMarkDifferent,
  onMarkSame,
  isReferenceSelected = false,
  onSelectReference,
}: DuplicateCandidateCardProps) => {
  const { t } = useTranslation();

  const title = useMemo(
    () => t("stakeholders.duplicateCheck.modal.potentialDuplicate", { index: index + 1, total }),
    [index, total, t]
  );

  const candidateDisplayData = useMemo(
    () => ({
      firstName: candidate.firstName?.stringValue,
      lastName: candidate.lastName?.stringValue,
      title: candidate.title?.stringValue,
      birthName: candidate.birthName?.stringValue,
      birthDate: candidate.birthDate?.stringValue,
      citizenshipCountryCode: candidate.citizenshipCountryCode?.stringValue,
      residenceCountryCode: candidate.residenceCountryCode?.stringValue,
      birthCountryCode: candidate.birthCountryCode?.stringValue,
      pepStatus: candidate.pepStatus,
      sanctionStatus: candidate.sanctionStatus,
    }),
    [candidate]
  );

  const isDifferentSelected = selectedDecision === "different";
  const isSameSelected = selectedDecision === "same";
  const isDifferentDimmed = !isDifferentSelected;
  const isSameDimmed = !isSameSelected;

  return (
    <div className="w-[340px] shrink-0">
      <div className="mb-10 relative flex items-center justify-center">
        {canGoPrev && (
          <Button
            type="button"
            variant="iconAction"
            size="none"
            className="absolute left-0 size-20"
            onClick={onPrev}
            aria-label="Voir le doublon précédent"
          >
            <NextIcon className="size-20 text-blue-info rotate-180" />
          </Button>
        )}

        <div className="flex flex-row items-center justify-center gap-x-10 text-center font-medium text-orange-medium">
          <CheckDuplicateIcon className="text-orange-medium w-20 h-20" />
          {title}
        </div>

        {canGoNext && (
          <Button
            type="button"
            variant="iconAction"
            size="none"
            className="absolute right-0 size-20"
            onClick={onNext}
            aria-label="Voir le doublon suivant"
          >
            <NextIcon className="size-20 text-blue-info" />
          </Button>
        )}
      </div>

      <StakeholderCard
        data={candidateDisplayData}
        countryWithRiskOptions={countryWithRiskOptions}
        customerCompanyNames={customerCompanyNames}
        customerCompaniesLoading={customerCompaniesLoading}
        withBottomBorder
        footer={
          <div className="grid grid-cols-2 gap-x-40 h-fit font-light">
            <Button
              variant="simpleLink"
              size="none"
              onClick={onMarkDifferent}
              className={isDifferentDimmed ? "opacity-40" : "opacity-100"}
              aria-pressed={isDifferentSelected}
            >
              <div className="flex flex-row items-center gap-x-5 text-sm">
                <CloseFullIcon className="size-20" />
                {t("stakeholders.duplicateCheck.modal.form.differentStakeholder")}
              </div>
            </Button>

            <Button
              variant="simpleLink"
              size="none"
              onClick={onMarkSame}
              className={isSameDimmed ? "opacity-40" : "opacity-100"}
              aria-pressed={isSameSelected}
            >
              <div className="flex flex-row items-center gap-x-5 text-sm">
                <CheckIcon className={cn("size-20", isSameSelected && "text-blue-info")} />
                {t("stakeholders.duplicateCheck.modal.form.sameStakeholder")}
              </div>
            </Button>
          </div>
        }
      />

      {isSameSelected && (
        <div className="flex flex-col items-center justify-center w-full mt-10">
          <Button
            variant="button4"
            size="none"
            data-selected={isReferenceSelected}
            onClick={onSelectReference}
          >
            <div className="flex flex-row items-center gap-x-5 text-sm">
              <CheckIcon className="size-20" />
              {t("stakeholders.duplicateCheck.modal.form.referenceProfile")}
            </div>
          </Button>
        </div>
      )}
    </div>
  );
};
